import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Login from "../pages/Auth/Login";
import Register from "../pages/Auth/Register";
import Layout from "../components/layout/Layout";
import SocketProvider from "../realtime/SocketProvider";

/* ── EVERY PAGE IS ITS OWN CHUNK ────────────────────────────────────────────────────────
 * These were 23 static imports, which meant one 1.35 MB bundle holding every screen in the
 * portal: the doctor downloaded the analytics charts, the EMR, the lab viewer and the whole
 * consultation workspace before they could see the login box. Splitting per route means a
 * screen is fetched the first time it is opened and cached from then on.
 *
 * Login and Register stay eager on purpose — they are the first paint for a signed-out
 * doctor, and putting a spinner in front of the login box to save bytes they are about to
 * need anyway is a worse trade. */
const DoctorHome = lazy(() => import("../pages/Doctor/Home"));
const PatientJourney = lazy(() => import("../pages/Doctor/PatientJourney"));
const TermsAndConditon = lazy(() => import("../pages/Doctor/TermsAndConditon"));
const PatientRecord = lazy(() => import("../pages/Doctor/PatientRecord"));
const AddPatient = lazy(() => import("../pages/Doctor/AddPatient"));
const Verification = lazy(() => import("../pages/Doctor/Verification"));
const EditProfile = lazy(() => import("../pages/Doctor/EditProfile"));
const Notifications = lazy(() => import("../pages/Doctor/Notifications"));
const Requests = lazy(() => import("../pages/Doctor/Requests"));
const PhysicianOnboarding = lazy(() => import("../pages/Doctor/PhysicianOnboarding"));
const CuoreCareConsole = lazy(() => import("../pages/Doctor/CuoreCare/Console"));
const CuoreCareConsultation = lazy(() => import("../pages/Doctor/CuoreCare/Consultation"));
const CuoreCareHistory = lazy(() => import("../pages/Doctor/CuoreCare/History"));
const CuoreCareAvailability = lazy(() => import("../pages/Doctor/CuoreCare/Availability"));
const EmrQueue = lazy(() => import("../pages/Doctor/EMR/Queue"));
const LabResults = lazy(() => import("../pages/Doctor/LabResults"));
const Professional = lazy(() => import("../pages/Doctor/Professional"));
const DaySchedule = lazy(() => import("../pages/Doctor/DaySchedule"));
const Analytics = lazy(() => import("../pages/Doctor/Analytics"));
const EmrRegisterPatient = lazy(() => import("../pages/Doctor/EMR/RegisterPatient"));
const EmrConsultation = lazy(() => import("../pages/Doctor/EMR/Consultation"));
const Settings = lazy(() => import("../pages/Doctor/Settings"));
// Clinic-chain admin / corporate client (CuoreMD item 22) — own login, no doctor sidebar.
const OrgLogin = lazy(() => import("../pages/Org/OrgLogin"));
const OrgDashboard = lazy(() => import("../pages/Org/OrgDashboard"));

/* A route's chunk arrives in milliseconds on a warm connection, so this is mostly invisible;
 * it exists so the first visit to a screen is never a blank frame. Deliberately quiet — a
 * large spinner between every navigation would undo the point of making navigation faster. */
function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24">
      <span className="w-6 h-6 rounded-full border-2 border-slate-300 border-t-brand-500 animate-spin" />
    </div>
  );
}

/* A doctor with CuoreCare works out of the console; only a doctor without it has the Patients
 * Health Overview as a home. The flag is cached at login (see landingFor in Auth/Login.jsx) so
 * this decides without waiting on a profile fetch and never flashes the wrong page. */
function HomeLanding() {
  if (localStorage.getItem("isCuoreCareDoctor") === "1") return <Navigate to="/cuorecare" replace />;
  return (
    <Layout>
      <DoctorHome />
    </Layout>
  );
}

const AppRoutes = () => {
  return (
    <Router>
      <SocketProvider>
      {/* One boundary around the whole route table rather than one per route: a navigation
          only ever resolves a single chunk, and a boundary per route would remount the
          fallback on each one. */}
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public / Auth */}
        <Route path="/"           element={<Login />} />
        <Route path="/login"      element={<Login />} />
        <Route path="/register"   element={<Register />} />
        <Route path="/onboarding" element={<PhysicianOnboarding />} />
        <Route path="/terms"      element={<TermsAndConditon />} />
        <Route path="/org/login"  element={<OrgLogin />} />
        <Route path="/org"        element={<OrgDashboard />} />

        {/* Protected — wrapped in sidebar Layout */}
        {/* "Home" for a CuoreCare doctor IS CuoreCare — Today. Login already landed there, but
            every other "go home" in the app (saving the profile, finishing Add Patient, the
            back button on a patient record) still dropped onto the Patients Health Overview
            dashboard, so the doctor kept being sent somewhere that isn't their day. This makes
            the whole app agree on one home. The overview itself is untouched and keeps its own
            path — /doctor-home, which the sidebar points at. */}
        <Route path="/home"              element={<HomeLanding />} />
        <Route path="/doctor-home"       element={<Layout><DoctorHome /></Layout>} />
        <Route path="/patient/:id"       element={<Layout><PatientJourney /></Layout>} />
        <Route path="/patientrecord"     element={<Layout><PatientRecord /></Layout>} />
        {/* URL-addressable form of the same page. The stateless "/patientrecord" above is
            reached via navigate(..., { state: { patient, ID } }) from the dashboard, but React
            Router state does NOT survive window.open / target="_blank" — a new window starts
            with an empty history entry. The consult screens need to open the record in a second
            window, so the id has to travel in the URL and the page has to fetch itself. Both
            forms share one component; the old callers are untouched. */}
        <Route path="/patientrecord/:id" element={<Layout><PatientRecord /></Layout>} />
        <Route path="/addpatient"        element={<Layout><AddPatient /></Layout>} />
        <Route path="/editprofile"       element={<Layout><EditProfile /></Layout>} />
        {/* §9/§10/§11 — KYC and verification. Reachable by an UNVERIFIED doctor by design:
            this is the screen that lets them become verified, so gating it would deadlock. */}
        <Route path="/verification"      element={<Layout><Verification /></Layout>} />
        <Route path="/notifications"     element={<Layout><Notifications /></Layout>} />
        {/* Requests get their own page rather than living as a tab inside CuoreCare —
            they span walk-in and teleconsult, and reception needs them too. */}
        <Route path="/requests"          element={<Layout><Requests /></Layout>} />
        <Route path="/termsandcondition" element={<Layout><TermsAndConditon /></Layout>} />
        {/* Appearance only — theme and text size. Open to reception as well as the doctor;
            it touches nothing but this browser. */}
        <Route path="/settings"         element={<Layout><Settings /></Layout>} />

        {/* CuoreCare (doctor console — gated inside the page on isCuoreCareDoctor) */}
        <Route path="/cuorecare" element={<Layout><CuoreCareConsole /></Layout>} />
        <Route path="/cuorecare/history" element={<Layout><CuoreCareHistory /></Layout>} />
        <Route path="/cuorecare/availability" element={<Layout><CuoreCareAvailability /></Layout>} />
        <Route path="/cuorecare/consultation/:id" element={<Layout><CuoreCareConsultation /></Layout>} />

        {/* Walk-in patient registration now lives under CuoreCare (its "Add Patient" tab) —
            walk-ins and tele-consults share the same Today/Upcoming/Completed queue, since a
            walk-in is an Appointment with mode "in_person" the same as a teleconsult.
            /emr/patients/new stays mounted as a legacy alias (same component, same title). */}
        <Route path="/cuorecare/patients/new" element={<Layout><EmrRegisterPatient /></Layout>} />
        <Route path="/emr/patients/new" element={<Layout><EmrRegisterPatient /></Layout>} />
        {/* The walk-in consultation workspace (adds Vaccination/Billing tabs a teleconsult
            doesn't need) — reached from CuoreCare's Today/Upcoming when a row's mode is
            "in_person". /emr itself (the old standalone queue dashboard) is no longer linked
            from the sidebar, but stays mounted rather than 404ing a bookmark. */}
        <Route path="/emr" element={<Layout><EmrQueue /></Layout>} />
        <Route path="/emr/visit/:id" element={<Layout><EmrConsultation /></Layout>} />

        {/* Lab Results & Diagnostics (reviewer item 2j) — a page of its own rather than a tab
            inside a consultation, because results arrive from an outside lab independently of
            any visit. Opens with a patient search; accepts ?patientId=&patientName= so the
            patient record or a consult screen can deep-link into it. */}
        <Route path="/diagnostics" element={<Layout><LabResults /></Layout>} />
        {/* Analytics (CuoreMD spec) — revenue by day/week/month/year, walk-in vs
            tele-consult. Gated on either capability (see Sidebar.jsx). */}
        {/* The day diary. Reachable by reception as well as the doctor — the backend scopes it. */}
        <Route path="/schedule"          element={<Layout><DaySchedule /></Layout>} />

        {/* CuoreMD Professional — the doctor-to-doctor network. */}
        <Route path="/professional"      element={<Layout><Professional /></Layout>} />

        <Route path="/analytics" element={<Layout><Analytics /></Layout>} />
        {/* §6.1: the section is now "Diagnostics". /labresults stays mounted rather than
            redirecting — a doctor may have bookmarked it, and a 404 is a worse rename than a
            duplicate path. Both render the same page, so there is nothing to keep in sync. */}
        <Route path="/labresults" element={<Layout><LabResults /></Layout>} />
      </Routes>
      </Suspense>
      </SocketProvider>
    </Router>
  );
};

export default AppRoutes;
